import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { getRequiredFields } from "components/form/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateOpening } from "../create/mutation";
import { useUpdateOpening } from "../update/mutation";
import { fetchOpeningDataforForm } from "./read";
import { openingSchema } from "./schema";

export const useOpeningForm = ({ openingId }) => {
    const navigate = useNavigate();

    const createOpeningMutation = useCreateOpening();
    const updateOpeningMutation = useUpdateOpening();

    const form = useForm({
        resolver: zodResolver(openingSchema),
        defaultValues: {
            skills: [],
            status: "active",
            sales_coordinator: "",
            expertise: "",
            opportunity_link: "",
        },
    });

    const requiredFields = useMemo(
        () => getRequiredFields(openingSchema),
        [openingSchema],
    );

    async function handleCreate(context) {
        const values = context.values;
        const response = await createOpeningMutation.mutateAsync({
            values,
        });
        navigate(`/apps/openings/${response.id}`);
    }

    async function handleRead(id) {
        const response = await fetchOpeningDataforForm({ id });
        return {
            ...response,
            skills: response?.skills?.map((s) => s.skill?.id).filter(Boolean) || [],
        };
    }

    async function handleUpdate(id, context) {
        const values = context.dirtyValues;
        const response = await updateOpeningMutation.mutateAsync({
            openingId: id,
            values,
        });
        navigate(-1);
    }

    async function handleArchive(id) {
        await updateOpeningMutation.mutateAsync({
            openingId: id,
            values: { status: "archived" },
        });
        navigate(-1);
    }

    return {
        form,
        requiredFields,
        handleCreate,
        handleRead,
        handleUpdate,
        handleArchive,
    };
};
